"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils/shared/core-utils";
import { DevModeSwitch } from "@/components/shared/common/DevModeSwitch";
import { ThemeToggleBulb } from "@/components/shared/common/ThemeToggleBulb";
import { MobileBackButton } from "@/components/shared/common/MobileBackButton";

// UserButton을 클라이언트 사이드에서만 렌더링하여 hydration 오류 방지
const UserButton = dynamic(
  () => import("@clerk/nextjs").then((mod) => mod.UserButton),
  { ssr: false }
);

/**
 * @file AppHeader.tsx
 * @description 앱 페이지용 헤더 컴포넌트
 *
 * 용도: 앱 페이지 (`/analysis`, `/dashboard`, `/simulations`, `/features` 등)
 * 디자인: sticky, 흰색 배경, 네비게이션 메뉴 중심
 *
 * 주요 기능:
 * - 로고 + 네비게이션 메뉴 (Desktop)
 * - 테마 토글 (다크/라이트 모드)
 * - 개발자 모드 스위치 (개발 서버에서만)
 * - UserButton
 */

export function AppHeader() {
  const pathname = usePathname();

  // 프로젝트 실제 라우팅에 맞춘 메뉴 구성
  const navItems = [
    { name: "경매 찾기", href: "/list" },
    { name: "기능 소개", href: "/features" },
    { name: "손익 계산", href: "/dashboard?section=calculator" },
    { name: "시뮬레이션", href: "/simulations" },
    { name: "내 검토 기록", href: "/dashboard" },
  ];

  // 랜딩 페이지가 아닐 때만 뒤로가기 버튼 표시
  const showBackButton = pathname !== "/" && pathname !== "/brand-story";

  return (
    <header className="sticky top-0 z-50 w-full border-b border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-md print:hidden">
      <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-4 lg:px-8">
        {/* 1. 로고 & 네비게이션 */}
        <div className="flex items-center gap-3 sm:gap-8">
          {/* 모바일 뒤로가기 버튼 */}
          {showBackButton && (
            <MobileBackButton className="shrink-0" />
          )}
          
          <Link
            href="/"
            className="font-black text-xl tracking-tighter text-zinc-900 dark:text-zinc-100 hover:opacity-80 transition-opacity shrink-0"
            onClick={() => window.scrollTo(0, 0)}
          >
            BIDIX
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((item) => {
              const isActive =
                item.href === "/"
                  ? pathname === "/"
                  : pathname.startsWith(item.href);

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-zinc-900 dark:hover:text-zinc-100",
                    // 현재 경로와 일치하거나, 해당 경로의 하위 페이지일 경우 활성화 스타일 적용
                    isActive 
                      ? "text-zinc-900 dark:text-zinc-100 font-bold" 
                      : "text-zinc-500 dark:text-zinc-400",
                  )}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* 2. 우측 컨트롤 (테마 토글, 개발자 모드, 유저 메뉴) */}
        <div className="flex items-center gap-4">
          {/* 테마 토글 (전구 메타포) */}
          <ThemeToggleBulb />

          {/* 개발자 모드 스위치 (개발 서버에서만 표시) */}
          <DevModeSwitch />

          {/* UserButton */}
          <UserButton
            afterSignOutUrl="/"
            appearance={{
              elements: { avatarBox: "h-8 w-8" },
            }}
          />
        </div>
      </div>
    </header>
  );
}
